<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <v-overlay :value="isLoading" z-index="999" :absolute="embedded">
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded" class="mt-4">
          <v-btn
            v-if="$route.name != `ProfilePage`"
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >{{ $route.name == "ProfilePage" ? "My Profile" : "Edit User" }}
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="form.first_name"
                outlined
                prepend-inner-icon="mdi-buffet"
                :error-messages="errors.first_name"
              >
                <template v-slot:label>First Name <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.middle_name"
                outlined
                label="Middle Name"
                prepend-inner-icon="mdi-buffet"
                :error-messages="errors.middle_name"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.last_name"
                outlined
                label="Last Name"
                prepend-inner-icon="mdi-buffet"
                :error-messages="errors.last_name"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.email"
                outlined
                :disabled="userRoleName !== 'ADMIN' && userRoleName !== 'SUPER ADMIN'"
                prepend-inner-icon="mdi-email"
                :error-messages="errors.email"
              >
                <template v-slot:label>Email <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.phone"
                outlined
                :disabled="userRoleName !== 'ADMIN' && userRoleName !== 'SUPER ADMIN'"
                prepend-inner-icon="mdi-phone"
                :error-messages="errors.phone"
              >
                <template v-slot:label>Phone <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4" v-if="$route.name != 'ProfilePage'">
              <v-autocomplete
                ref="position"
                outlined
                v-model="form.position_id"
                :items="positionItems"
                prepend-inner-icon="mdi-account-tie-outline"
                clearable
                :error-messages="errors.position_id"
              >
                <template v-slot:label>Position <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-autocomplete>
            </v-col>
          </v-row>
          <v-divider class="my-2"> </v-divider>
          <h3 class="my-4">Account Status:</h3>
          <v-row>
            <v-col sm="3">
              <v-switch
                class="mt-0"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
              ></v-switch>
            </v-col>
          </v-row>
          <v-divider class="my-5"> </v-divider>
          <v-btn
            v-if="$route.name !== 'ProfilePage'"
            text
            class="mr-2"
            @click="embedded ? $emit('close') : $router.back()"
          >Cancel</v-btn>
          <v-btn
            :loading="isLoading"
            :disabled="isLoading"
            color="primary"
            rounded
            dark
            @click="save"
          >
            Update {{ $route.name == "ProfilePage" ? `Profile` : `User` }}
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Change Password - self-service only, not shown when an admin is
           editing someone else's user record via /users/:id -->
      <v-card v-if="$route.name == 'ProfilePage'" elevation="6" rounded="xxl" class="mt-6">
        <v-card-title class="mt-4">
          <span>Change Password</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="passwordForm.current_password"
                outlined
                :append-icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showCurrentPassword ? 'text' : 'password'"
                prepend-inner-icon="mdi-lock-outline"
                @click:append="showCurrentPassword = !showCurrentPassword"
                :error-messages="passwordErrors.current_password"
              >
                <template v-slot:label>Current Password <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="passwordForm.new_password"
                outlined
                :append-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showNewPassword ? 'text' : 'password'"
                prepend-inner-icon="mdi-lock-plus-outline"
                hint="At least 6 characters"
                persistent-hint
                @click:append="showNewPassword = !showNewPassword"
                :error-messages="passwordErrors.new_password"
              >
                <template v-slot:label>New Password <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="passwordForm.new_password_confirmation"
                outlined
                :append-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showConfirmPassword ? 'text' : 'password'"
                prepend-inner-icon="mdi-lock-check-outline"
                @click:append="showConfirmPassword = !showConfirmPassword"
                :error-messages="passwordErrors.new_password_confirmation"
              >
                <template v-slot:label>Confirm New Password <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-btn
            :loading="isSavingPassword"
            :disabled="isSavingPassword"
            color="primary"
            rounded
            dark
            @click="savePassword"
          >
            Update Password
          </v-btn>
        </v-card-text>
      </v-card>
    </component>

    <!-- Success Dialog - not shown for embedded (dialog) use, which closes
         and lets the parent list refresh instead. -->
    <v-dialog v-if="!embedded" v-model="successDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon style="color: green; font-size: 90px">mdi-check-circle</v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: green">Success!</h1>
                <br />
                <h3>{{ $route.name == "ProfilePage" ? "Profile" : "User" }} updated successfully.</h3>
                <br />
                <v-btn color="success" @click="successDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </component>
</template>
<script>
import axios from "axios";
export default {
  name: "UserEditPage",
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Users list's inline "Edit User") instead of its own routed page -
    // the id comes from the `userId` prop rather than the route, and it
    // emits `saved`/`close` rather than navigating away.
    embedded: { type: Boolean, default: false },
    userId: { type: String, default: null },
  },
  data() {
    return {
      form: {},
      isLoading: false,
      successDialog: false,
      positionItems: [],
      uplineUserItems: [],
      supervisorDownlined: [
        "Field Executive",
        "Merchandiser",
        "Promoter",
        "Van Sales",
      ],
      typeItems: [
        { id: "PENDING", text: "PENDING", value: 1 },
        { id: "APPROVED", text: "APPROVED", value: 0 },
      ],
      old_json: {},
      // Change Password - kept on its own local state (not the shared
      // errors store the main profile form uses) so the two forms never
      // clobber each other's validation messages.
      passwordForm: {
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
      },
      passwordErrors: {},
      isSavingPassword: false,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
    };
  },
  computed: {
    user() {
      return this.$store.state.auth?.user || {};
    },
    // `user.roles[0].name` directly in the template throws whenever roles
    // hasn't loaded yet (e.g. right after a hard reload of this page, before
    // Vuex re-hydrates the logged-in user) - a render-time crash there was
    // leaving the page's layout registration (drawer/content offset) broken
    // for the rest of the page's life. Guard it here instead.
    userRoleName() {
      return this.user.roles && this.user.roles[0] ? this.user.roles[0].name : null;
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
    this.getMasters();
    this.getData();
  },
  methods: {
    async getMasters() {
      await axios
        .get(`masters?context=user`)
        .then((masters) => {
          masters = masters.data;
          //Position
          masters.positions.forEach((position) => {
            this.positionItems.push({
              id: position.id,
              text: position.name,
              value: position.id,
            });
          });
        })
        .catch(function (error) {
          console.log(error);
          // redirect to Home Page
          // window.location.href = "/auth/login";
        });
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.userId || this.$route.params.userId;
      await axios
        .get(`/users/${encryptedId}`)
        .then((response) => {
          this.form = response.data.data;
          this.old_json = response.data.data;
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
      this.isLoading = false;
    },
    async save() {
      this.isLoading = true;
      this.$store.dispatch("errors/setErrors", {});
      const encryptedId = this.userId || this.$route.params.userId;
      try {
        await axios.patch(`/users/${encryptedId}`, this.form);
        await this.saveUserTimestamp(this.old_json, this.form);
        if (this.embedded) {
          this.$emit("saved");
          this.$emit("close");
        } else {
          this.successDialog = true;
          if (this.$route.name !== "ProfilePage") {
            setTimeout(() => this.$router.push("/users"), 1200);
          }
        }
      } catch (error) {
        console.error("Error updating user:", error);
        if (error.response && error.response.data && error.response.data.errors) {
          this.$store.dispatch("errors/setErrors", error.response.data.errors);
        }
      } finally {
        this.isLoading = false;
      }
    },
    async savePassword() {
      this.passwordErrors = {};
      if (this.passwordForm.new_password !== this.passwordForm.new_password_confirmation) {
        this.passwordErrors = {
          new_password_confirmation: ["New password and confirmation do not match."],
        };
        return;
      }

      this.isSavingPassword = true;
      try {
        await axios.post("/profile/update-password", this.passwordForm);
        this.passwordForm = {
          current_password: "",
          new_password: "",
          new_password_confirmation: "",
        };
        if (this.$toast) this.$toast.success("Password updated successfully.");
      } catch (error) {
        console.error("Error updating password:", error);
        if (error.response && error.response.data && error.response.data.errors) {
          this.passwordErrors = error.response.data.errors;
        } else if (this.$toast) {
          this.$toast.error("Failed to update password.");
        }
      } finally {
        this.isSavingPassword = false;
      }
    },
  },
};
</script>
